import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import {
  createPopupMessage,
  deletePopupMessage,
  fetchPopupMessages,
  setPopupMessageEnabled,
  updatePopupMessage,
} from '../api/popupMessages';

const LANGUAGES = [
  { code: 'ru', label: 'Русский' },
  { code: 'tm', label: 'Türkmençe' },
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Türkçe' },
];

const EMPTY_TRANSLATIONS = { ru: '', tm: '', en: '', tr: '' };
const EMPTY_FORM = {
  name: '',
  enabled: true,
  placement: 'home',
  priority: 0,
  starts_at: '',
  ends_at: '',
  delay_ms: 0,
  duration_ms: 0,
  max_impressions_per_browser: '',
  css: '',
  translations: EMPTY_TRANSLATIONS,
};

function toLocalDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function getTranslationValues(message) {
  const source = message.translations || message.contents || message.content || {};
  return LANGUAGES.reduce((result, language) => {
    const value = source[language.code];
    result[language.code] = typeof value === 'object'
      ? value?.html || value?.content || value?.body || ''
      : value || message[`html_${language.code}`] || '';
    return result;
  }, {});
}

function toForm(message) {
  if (!message) return { ...EMPTY_FORM, translations: { ...EMPTY_TRANSLATIONS } };
  return {
    name: message.name || message.title || '',
    enabled: Boolean(message.enabled),
    placement: message.placement || 'home',
    priority: Number(message.priority ?? 0),
    starts_at: toLocalDateTime(message.starts_at ?? message.start_at),
    ends_at: toLocalDateTime(message.ends_at ?? message.end_at),
    delay_ms: Number(message.delay_ms ?? 0),
    duration_ms: Number(message.duration_ms ?? 0),
    max_impressions_per_browser: message.max_impressions_per_browser
      ?? message.max_impressions
      ?? '',
    css: message.css || '',
    translations: getTranslationValues(message),
  };
}

function toPayload(form) {
  const maxImpressions = form.max_impressions_per_browser;
  return {
    name: form.name.trim(),
    enabled: Boolean(form.enabled),
    placement: form.placement,
    priority: Number(form.priority) || 0,
    starts_at: form.starts_at || null,
    ends_at: form.ends_at || null,
    delay_ms: Math.max(0, Number(form.delay_ms) || 0),
    duration_ms: Math.max(0, Number(form.duration_ms) || 0),
    max_impressions_per_browser: maxImpressions === '' || Number(maxImpressions) === 0
      ? null
      : Math.max(1, Number(maxImpressions)),
    css: form.css,
    translations: { ...form.translations },
  };
}

function buildPreviewDocument(html, css) {
  const safeCss = String(css || '').replace(/<\/style/gi, '<\\/style');
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      html, body { margin: 0; min-height: 100%; font-family: Arial, sans-serif; }
      body { box-sizing: border-box; padding: 16px; overflow-wrap: anywhere; }
      *, *::before, *::after { box-sizing: border-box; }
      ${safeCss}
    </style>
  </head>
  <body>${html || ''}</body>
</html>`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function PopupMessagesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const token = user?.api_token;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, message: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(toForm());
  const [language, setLanguage] = useState('ru');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadMessages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      setMessages(await fetchPopupMessages(token));
    } catch (requestError) {
      setError(requestError.message || t('Ошибка загрузки сообщений'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const preview = useMemo(
    () => buildPreviewDocument(form.translations[language], form.css),
    [form.css, form.translations, language],
  );

  if (!user || user.role !== 'admin') {
    return <Alert severity="error">{t('Доступ запрещён. Только для администратора.')}</Alert>;
  }

  const openEditor = (message = null) => {
    setDialog({ open: true, message });
    setForm(toForm(message));
    setLanguage('ru');
  };

  const closeEditor = () => {
    if (!saving) setDialog({ open: false, message: null });
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return t('Укажите название сообщения');
    if (!form.translations.ru.trim()) {
      return t('Добавьте HTML на русском языке');
    }
    if (form.starts_at && form.ends_at && form.starts_at > form.ends_at) {
      return t('Дата окончания должна быть позже даты начала');
    }
    return '';
  };

  const saveMessage = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload(form);
      if (dialog.message) {
        await updatePopupMessage(token, dialog.message.id, payload);
      } else {
        await createPopupMessage(token, payload);
      }
      setSnackbar({
        open: true,
        message: t(dialog.message ? 'Сообщение обновлено' : 'Сообщение создано'),
        severity: 'success',
      });
      setDialog({ open: false, message: null });
      await loadMessages();
    } catch (requestError) {
      setSnackbar({ open: true, message: requestError.message || t('Ошибка сохранения сообщения'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (message, enabled) => {
    setMessages((current) => current.map((item) => (
      item.id === message.id ? { ...item, enabled } : item
    )));
    try {
      await setPopupMessageEnabled(token, message.id, enabled);
      setSnackbar({ open: true, message: t('Статус сообщения обновлён'), severity: 'success' });
    } catch (requestError) {
      setMessages((current) => current.map((item) => (
        item.id === message.id ? { ...item, enabled: message.enabled } : item
      )));
      setSnackbar({ open: true, message: requestError.message || t('Ошибка обновления статуса'), severity: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deletePopupMessage(token, deleteTarget.id);
      setDeleteTarget(null);
      setSnackbar({ open: true, message: t('Сообщение удалено'), severity: 'success' });
      await loadMessages();
    } catch (requestError) {
      setSnackbar({ open: true, message: requestError.message || t('Ошибка удаления'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('Сообщения')}</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('Обновить')}>
          <IconButton onClick={loadMessages} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditor()}>
          {t('Создать сообщение')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Название')}</TableCell>
              <TableCell>{t('Размещение')}</TableCell>
              <TableCell align="right">{t('Приоритет')}</TableCell>
              <TableCell>{t('Период показа')}</TableCell>
              <TableCell align="center">{t('Включено')}</TableCell>
              <TableCell align="right">{t('Действия')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell>
              </TableRow>
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {t('Сообщений пока нет')}
                </TableCell>
              </TableRow>
            ) : messages.map((message) => (
              <TableRow key={message.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{message.name || message.title || `#${message.id}`}</Typography>
                  <Typography variant="caption" color="text.secondary">ID: {message.id}</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={message.placement || 'home'} /></TableCell>
                <TableCell align="right">{message.priority ?? 0}</TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(message.starts_at ?? message.start_at)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('до')} {formatDate(message.ends_at ?? message.end_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={Boolean(message.enabled)}
                    onChange={(event) => toggleEnabled(message, event.target.checked)}
                    inputProps={{ 'aria-label': t('Включено') }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('Редактировать')}>
                    <IconButton onClick={() => openEditor(message)}><EditOutlinedIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title={t('Удалить')}>
                    <IconButton color="error" onClick={() => setDeleteTarget(message)}><DeleteOutlineIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={closeEditor} fullWidth maxWidth="lg">
        <DialogTitle>
          {t(dialog.message ? 'Редактировать сообщение' : 'Создать сообщение')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('Название')}
              value={form.name}
              onChange={(event) => setField('name', event.target.value)}
              required
              fullWidth
            />
            <TextField
              select
              label={t('Размещение')}
              value={form.placement}
              onChange={(event) => setField('placement', event.target.value)}
              fullWidth
            >
              <MenuItem value="home">home</MenuItem>
            </TextField>
            <TextField
              label={t('Приоритет')}
              type="number"
              value={form.priority}
              onChange={(event) => setField('priority', event.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={(event) => setField('enabled', event.target.checked)} />}
              label={t('Включено')}
              sx={{ px: 1 }}
            />
            <TextField
              label={t('Начало показа')}
              type="datetime-local"
              value={form.starts_at}
              onChange={(event) => setField('starts_at', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t('Окончание показа')}
              type="datetime-local"
              value={form.ends_at}
              onChange={(event) => setField('ends_at', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t('Задержка (мс)')}
              type="number"
              value={form.delay_ms}
              onChange={(event) => setField('delay_ms', event.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label={t('Длительность (мс)')}
              type="number"
              value={form.duration_ms}
              onChange={(event) => setField('duration_ms', event.target.value)}
              helperText={t('0 — не закрывать автоматически')}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label={t('Максимум показов на браузер')}
              type="number"
              value={form.max_impressions_per_browser}
              onChange={(event) => setField('max_impressions_per_browser', event.target.value)}
              helperText={t('Пусто или 0 — без ограничения')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Box>

          <Paper variant="outlined" sx={{ mt: 3 }}>
            <Tabs value={language} onChange={(_, value) => setLanguage(value)} variant="scrollable">
              {LANGUAGES.map((item) => <Tab key={item.code} value={item.code} label={item.label} />)}
            </Tabs>
            <Box sx={{ p: 2 }}>
              <TextField
                label={`${t('HTML сообщения')} (${language.toUpperCase()})`}
                value={form.translations[language]}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  translations: { ...current.translations, [language]: event.target.value },
                }))}
                multiline
                minRows={10}
                fullWidth
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </Box>
          </Paper>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('Дополнительный CSS')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="CSS"
                value={form.css}
                onChange={(event) => setField('css', event.target.value)}
                multiline
                minRows={6}
                fullWidth
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </AccordionDetails>
          </Accordion>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            {t('Предпросмотр')} ({language.toUpperCase()})
          </Typography>
          <Box
            component="iframe"
            title={t('Предпросмотр сообщения')}
            sandbox=""
            srcDoc={preview}
            sx={{ width: '100%', minHeight: 260, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fff' }}
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('Предпросмотр изолирован: скрипты и доступ к странице отключены.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>{t('Отмена')}</Button>
          <Button onClick={saveMessage} variant="contained" disabled={saving}>
            {saving ? t('Сохранение...') : t('Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>{t('Удалить сообщение?')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('Сообщение будет удалено без возможности восстановления.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>{t('Отмена')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={saving}>
            {t('Удалить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
